import type { RadioCardItemProps } from "..";

/* *
 * Return singular or plural form of a word based on the count
 * e.g. pluralize(1, "car", "cars") returns "car"
 *      pluralize(3, "car", "cars") returns "cars"
 */
export const pluralize = (count: number, singular: string, plural: string) => {
  return count === 1 ? singular : plural;
};

/* *
 * Capitalise each word in the `title` string
 * e.g. "this title" becomes "This Title"
 */
export const titlecase = (title: string): string => {
  return title.replace(/\b\p{L}/gu, c => c.toUpperCase());
};

/**
 * Converts a score into a display-friendly percentage value.
 *
 * - If the score is less than 1, it keeps one decimal place.
 * - If the score is 1 or greater, it returns the whole number by flooring the value.
 *
 * @param score - The numeric score to format.
 * @returns A number representing the formatted percentage value.
 */
export const displayPercentageValue = (score: number): number => {
  if (score < 1) {
    return parseFloat(score.toFixed(1));
  }
  return Math.floor(score);
};

/**
 * Truncates a file name if it exceeds a given max length,
 * keeping the file extension visible and adding "..." in the middle.
 *
 * @param fileName - The full file name to truncate.
 * @param maxLength - The maximum allowed length of the result (default is 24).
 * @returns The truncated file name, or the original if it's short enough.
 */
export const truncateFileName = (fileName: string, maxLength: number = 24) => {
  if (fileName.length <= maxLength) return fileName;
  const ext = fileName.slice(fileName.lastIndexOf("."));
  const frontChars = Math.ceil((maxLength - ext.length) / 2);
  const backChars = maxLength - ext.length - frontChars;
  return `${fileName.slice(0, frontChars)}...${fileName.slice(-backChars)}`;
};

/**
 * Marks avatar options that are already in use by other family members as disabled.
 *
 * Pass the full avatar list and the keys used by siblings (excluding the current
 * student's own key). Each matched item gets `disabled: true` and a
 * `disabledReason` tooltip explaining why it cannot be selected.
 *
 * @param avatars  - The full list of avatar `RadioCardItemProps` to evaluate.
 * @param usedKeys - Avatar key numbers already claimed by other family members.
 * @returns        A new avatar list with conflicting items marked as disabled.
 */
export const markUsedAvatars = (
  avatars: RadioCardItemProps[],
  usedKeys: number[]
) =>
  avatars.map(a => ({
    ...a,
    value: a.value as number,
    disabled: usedKeys.includes(Number(a.value)),
    disabledReason: usedKeys.includes(Number(a.value))
      ? "You cannot select this character as it is being used by another student in your family account."
      : ""
  }));
